import type { DashboardsDashboard, DashboardsWidgetConfig } from '@smarthome/api';
import {
  Eye,
  EyeOff,
  GripVertical,
  Pencil,
  Plus,
  RotateCcw,
  Save,
  LayoutGrid,
  Code,
} from 'lucide-react';
import { memo, useCallback, useMemo, useState } from 'react';
import { ReactGridLayout as GridLayout, type Layout } from 'react-grid-layout/legacy';

import { getWidgetDefinition } from '../registry';
import type { EditorWidget, WidgetType } from '../types';
import { DashboardViewer } from '../viewer';
import type { DashboardTimeRange } from '../viewer/DashboardTimeRangeContext';
import {
  DashboardTimeRangeProvider,
  DEFAULT_DASHBOARD_TIME_RANGE,
} from '../viewer/DashboardTimeRangeContext';
import { GRID_COLUMNS, ROW_HEIGHT_PX } from '../viewer/layout';
import { TimeRangeSelector } from '../viewer/TimeRangeSelector';
import { WidgetFrame } from '../viewer/WidgetFrame';

import { AddWidgetModal } from './AddWidgetModal';
import { CodeEditor } from './CodeEditor';
import { EditWidgetModal } from './EditWidgetModal';
import { useDashboardDraft } from './useDashboardDraft';

import { Button } from '@/components/ui';

import 'react-grid-layout/css/styles.css';
// Custom resize handle styles are defined in index.css

type EditorTab = 'visual' | 'code';

export interface DashboardEditorProps {
  dashboard: DashboardsDashboard;
  onSave: (document: {
    schemaVersion: number;
    timeRange?: string;
    widgets: EditorWidget[];
  }) => void;
  onCancel: () => void;
  isSaving?: boolean;
}

const GRID_GAP = 16;

export function DashboardEditor({ dashboard, onSave, onCancel, isSaving }: DashboardEditorProps) {
  const [activeTab, setActiveTab] = useState<EditorTab>('visual');
  const [isPreview, setIsPreview] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingWidget, setEditingWidget] = useState<EditorWidget | null>(null);
  const [containerWidth, setContainerWidth] = useState(1200);

  const {
    draft,
    isDirty,
    addWidget,
    updateWidgetLayout,
    updateWidgetTitle,
    updateWidgetConfig,
    deleteWidget,
    resetDraft,
    updateTimeRange,
    setDocument,
    getDocument,
  } = useDashboardDraft(dashboard);

  // Compute whether any widget uses dashboard time range mode
  const showTimeRangeSelector = useMemo(() => {
    return draft.widgets.some((w) => {
      const def = getWidgetDefinition(w.type);
      if (!def?.supportsTimeRange) return false;
      const config = w.config as { timeRangeMode?: string } | null | undefined;
      return (config?.timeRangeMode ?? 'fixed') === 'dashboard';
    });
  }, [draft.widgets]);

  const effectiveTimeRange =
    (draft.timeRange as DashboardTimeRange) || DEFAULT_DASHBOARD_TIME_RANGE;

  // Convert editor widgets to react-grid-layout format
  const layout = useMemo(() => {
    return draft.widgets.map((widget) => {
      const def = getWidgetDefinition(widget.type);
      return {
        i: widget.id,
        x: widget.x,
        y: widget.y,
        w: widget.w,
        h: widget.h,
        minW: def?.minSize.w ?? 1,
        minH: def?.minSize.h ?? 1,
        maxW: def?.maxSize?.w,
        maxH: def?.maxSize?.h,
      };
    });
  }, [draft.widgets]);

  // Handle layout changes from react-grid-layout
  const handleLayoutChange = useCallback(
    (newLayout: Layout) => {
      for (const item of newLayout) {
        const widget = draft.widgets.find((w) => w.id === item.i);
        if (
          widget &&
          (widget.x !== item.x || widget.y !== item.y || widget.w !== item.w || widget.h !== item.h)
        ) {
          updateWidgetLayout(item.i, item.x, item.y, item.w, item.h);
        }
      }
    },
    [draft.widgets, updateWidgetLayout]
  );

  // Handle adding a new widget
  const handleAddWidget = useCallback(
    (type: WidgetType, title: string, config: DashboardsWidgetConfig) => {
      addWidget(type, title, config);
    },
    [addWidget]
  );

  // Handle saving widget edits
  const handleSaveWidget = useCallback(
    (id: string, title: string, config: DashboardsWidgetConfig) => {
      updateWidgetTitle(id, title);
      updateWidgetConfig(id, config);
    },
    [updateWidgetTitle, updateWidgetConfig]
  );

  // Handle deleting a widget
  const handleDeleteWidget = useCallback(
    (id: string) => {
      deleteWidget(id);
      setEditingWidget(null);
    },
    [deleteWidget]
  );

  // Handle save
  const handleSave = useCallback(() => {
    onSave(getDocument());
  }, [onSave, getDocument]);

  // Handle cancel with dirty check
  const handleCancel = useCallback(() => {
    if (isDirty) {
      if (!window.confirm('You have unsaved changes. Are you sure you want to discard them?')) {
        return;
      }
    }
    onCancel();
  }, [isDirty, onCancel]);

  // Handle resize stop: snap widget height to fill gaps within SNAP_ROWS
  const handleResizeStop = useCallback(
    (resizedLayout: Layout) => {
      const SNAP_ROWS = 2;
      const gridBottom = Math.max(...resizedLayout.map((item) => item.y + item.h));
      const snapped = resizedLayout.map((item) => {
        const itemBottom = item.y + item.h;
        const below = resizedLayout.filter(
          (other) =>
            other.i !== item.i &&
            other.y >= itemBottom &&
            other.x < item.x + item.w &&
            other.x + other.w > item.x
        );
        if (below.length > 0) {
          const nearestTop = Math.min(...below.map((w) => w.y));
          if (nearestTop - itemBottom > 0 && nearestTop - itemBottom <= SNAP_ROWS) {
            return { ...item, h: nearestTop - item.y };
          }
        } else if (gridBottom - itemBottom > 0 && gridBottom - itemBottom <= SNAP_ROWS) {
          return { ...item, h: gridBottom - item.y };
        }
        return item;
      });
      handleLayoutChange(snapped);
    },
    [handleLayoutChange]
  );

  // Handle applying code editor changes to draft
  const handleApplyFromCode = useCallback(
    (newDocument: { schemaVersion: number; widgets: EditorWidget[] }) => {
      setDocument(newDocument);
      setActiveTab('visual');
    },
    [setDocument]
  );

  // Measure container width
  const containerRef = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      const observer = new ResizeObserver((entries) => {
        for (const entry of entries) {
          setContainerWidth(entry.contentRect.width);
        }
      });
      observer.observe(node);
      return () => observer.disconnect();
    }
  }, []);

  // Preview mode: render the DashboardViewer
  if (isPreview) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between rounded-xl border border-gray-200 bg-white px-4 py-3 dark:border-white/[0.06] dark:bg-[#18181b]">
          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-zinc-400">
            <Eye className="h-4 w-4" />
            Preview Mode
          </div>
          <div className="flex items-center gap-3">
            {showTimeRangeSelector && (
              <TimeRangeSelector
                value={effectiveTimeRange}
                onChange={(value) => updateTimeRange(value)}
              />
            )}
            <Button variant="outline" size="sm" onClick={() => setIsPreview(false)}>
              <EyeOff className="mr-2 h-4 w-4" />
              Exit Preview
            </Button>
          </div>
        </div>
        <DashboardViewer
          document={{
            schemaVersion: draft.schemaVersion,
            timeRange: draft.timeRange,
            widgets: draft.widgets.map((w) => ({
              ...w,
              config: w.config,
            })),
          }}
          timeRange={effectiveTimeRange}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Tab switcher + save/cancel */}
      <div className="flex items-center justify-between rounded-xl border border-gray-200 bg-white px-4 py-3 dark:border-white/[0.06] dark:bg-[#18181b]">
        {/* Tabs */}
        <div
          role="tablist"
          aria-label="Editor mode"
          className="flex rounded-lg border border-gray-200 dark:border-white/[0.08]"
        >
          <button
            id="visual-tab"
            type="button"
            role="tab"
            aria-selected={activeTab === 'visual'}
            aria-controls="visual-editor-panel"
            onClick={() => setActiveTab('visual')}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium transition-colors ${
              activeTab === 'visual'
                ? 'bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300'
                : 'text-gray-600 hover:bg-gray-50 dark:text-zinc-400 dark:hover:bg-white/[0.04]'
            }`}
          >
            <LayoutGrid className="h-4 w-4" />
            Visual
          </button>
          <button
            id="code-tab"
            type="button"
            role="tab"
            aria-selected={activeTab === 'code'}
            aria-controls="code-editor-panel"
            onClick={() => setActiveTab('code')}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium transition-colors ${
              activeTab === 'code'
                ? 'bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300'
                : 'text-gray-600 hover:bg-gray-50 dark:text-zinc-400 dark:hover:bg-white/[0.04]'
            }`}
          >
            <Code className="h-4 w-4" />
            JSON/YAML
          </button>
        </div>

        {/* Save/Cancel buttons */}
        <div className="flex items-center gap-2">
          {isDirty && (
            <Button variant="ghost" size="sm" onClick={resetDraft} className="text-gray-500">
              <RotateCcw className="mr-2 h-4 w-4" />
              Reset
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={handleCancel}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleSave} loading={isSaving} disabled={!isDirty}>
            <Save className="mr-2 h-4 w-4" />
            Save
          </Button>
        </div>
      </div>

      {/* Visual Editor Tab */}
      {activeTab === 'visual' && (
        <div
          id="visual-editor-panel"
          role="tabpanel"
          aria-labelledby="visual-tab"
          className="space-y-4"
        >
          {/* Visual editor toolbar */}
          <div className="flex items-center justify-between rounded-xl border border-gray-200 bg-white px-4 py-3 dark:border-white/[0.06] dark:bg-[#18181b]">
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={() => setIsAddModalOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Add Widget
              </Button>
              <Button variant="outline" size="sm" onClick={() => setIsPreview(true)}>
                <Eye className="mr-2 h-4 w-4" />
                Preview
              </Button>
            </div>
            {showTimeRangeSelector && (
              <TimeRangeSelector
                value={effectiveTimeRange}
                onChange={(value) => updateTimeRange(value)}
              />
            )}
          </div>

          {/* Grid editor */}
          <DashboardTimeRangeProvider timeRange={effectiveTimeRange}>
            <div
              ref={containerRef}
              className="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-white/[0.06] dark:bg-zinc-900/50"
            >
              {draft.widgets.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100 dark:bg-zinc-800">
                    <Plus className="h-8 w-8 text-gray-400 dark:text-zinc-500" />
                  </div>
                  <p className="text-sm font-medium text-gray-700 dark:text-zinc-300">
                    No widgets yet
                  </p>
                  <p className="mt-1 text-xs text-gray-500 dark:text-zinc-500">
                    Click &ldquo;Add Widget&rdquo; to get started
                  </p>
                  <Button size="sm" className="mt-4" onClick={() => setIsAddModalOpen(true)}>
                    <Plus className="mr-2 h-4 w-4" />
                    Add Widget
                  </Button>
                </div>
              ) : (
                <GridLayout
                  className="layout"
                  layout={layout}
                  cols={GRID_COLUMNS}
                  rowHeight={ROW_HEIGHT_PX}
                  width={containerWidth - 32}
                  margin={[GRID_GAP, GRID_GAP]}
                  containerPadding={[0, 0]}
                  onLayoutChange={handleLayoutChange}
                  onResizeStop={handleResizeStop}
                  draggableHandle=".widget-drag-handle"
                  useCSSTransforms
                  compactType="vertical"
                  preventCollision={false}
                  isResizable={true}
                  resizeHandles={['se', 'e', 's']}
                >
                  {draft.widgets.map((widget) => (
                    <div key={widget.id} className="group">
                      <EditorWidgetWrapper
                        widget={widget}
                        onEdit={() => setEditingWidget(widget)}
                      />
                    </div>
                  ))}
                </GridLayout>
              )}
            </div>
          </DashboardTimeRangeProvider>
        </div>
      )}

      {/* Code Editor Tab */}
      {activeTab === 'code' && (
        <div id="code-editor-panel" role="tabpanel" aria-labelledby="code-tab">
          <CodeEditor document={draft} onApply={handleApplyFromCode} />
        </div>
      )}

      {/* Add widget modal */}
      <AddWidgetModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onAdd={handleAddWidget}
      />

      {/* Edit widget modal - key ensures fresh state for each widget */}
      <EditWidgetModal
        key={editingWidget?.id}
        widget={editingWidget}
        isOpen={!!editingWidget}
        onClose={() => setEditingWidget(null)}
        onSave={handleSaveWidget}
        onDelete={handleDeleteWidget}
      />
    </div>
  );
}

/**
 * Wrapper for widgets in edit mode with drag handle and edit button
 * Memoized to prevent re-renders during drag operations
 */
const EditorWidgetWrapper = memo(function EditorWidgetWrapper({
  widget,
  onEdit,
}: {
  widget: EditorWidget;
  onEdit: () => void;
}) {
  const def = getWidgetDefinition(widget.type);
  const Renderer = def?.Renderer;

  return (
    <div className="relative h-full">
      {/* Edit overlay - visible on hover */}
      <div className="absolute inset-0 z-10 flex items-start justify-between rounded-xl bg-transparent p-2 opacity-0 transition-opacity group-hover:opacity-100">
        {/* Drag handle */}
        <div
          className="widget-drag-handle flex cursor-move items-center justify-center rounded-lg bg-gray-900/60 p-1.5 text-white backdrop-blur-sm"
          role="button"
          aria-label={`Drag ${widget.title} widget to reposition`}
          tabIndex={0}
        >
          <GripVertical className="h-4 w-4" />
        </div>
        {/* Edit button */}
        <button
          type="button"
          onClick={onEdit}
          aria-label={`Edit ${widget.title} widget`}
          className="flex items-center justify-center rounded-lg bg-gray-900/60 p-1.5 text-white backdrop-blur-sm transition-colors hover:bg-gray-900/80"
        >
          <Pencil className="h-4 w-4" />
        </button>
      </div>

      {/* Widget content */}
      <WidgetFrame widget={widget}>
        {Renderer ? (
          <Renderer widget={widget} config={widget.config as typeof def.defaultConfig} />
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-gray-500 dark:text-zinc-500">
            Unknown widget type
          </div>
        )}
      </WidgetFrame>
    </div>
  );
});
